import { IAlertProps } from "@components/alert/Alert.types";
import Dialog from "@components/dialog";
import Field from "@components/inputs/field";
import FieldsWrapper from "@components/inputs/field/FieldsWrapper";
import Input, { IInputOnBlurEvent, IInputOnChangeEvent } from "@components/inputs/input";
import { IPrPayslipEntity } from "@odata/GeneratedEntityTypes";
import { ODataError, parseResponse } from "@odata/ODataParser";
import customFetch from "@utils/customFetch";
import React from "react";
import { WithTranslation, withTranslation } from "react-i18next";

import { EMAIL_REGEX, REST_API_URL } from "../../../constants";
import { BasicInputSizes, Status } from "../../../enums";
import { IValidationError } from "../../../model/Validator.types";
import { getAlertFromError } from "../../../views/formView/Form.utils";
import { FormStorage } from "../../../views/formView/FormStorage";
import ConfirmationButtons from "../../../views/table/ConfirmationButtons";
import { getAlertPropsByStatus } from "../../../views/table/TableView.utils";

interface IProps {
    storage: FormStorage;
    onClose: (alert?: IAlertProps) => void;
}

interface IState {
    error?: IValidationError;
    email?: string;
    isBusy?: boolean;
}

const SEND_PAYSLIP_EMAIL_URL = `${REST_API_URL}/PrPayslipEmail`;

class SendPayslipByEmailDialog extends React.Component<IProps & WithTranslation, IState> {

    constructor(props: IProps & WithTranslation) {
        super(props);

        const payslip = props.storage.data.entity as IPrPayslipEntity;
        this.state = {
            error: null,
            email: payslip.Employee?.Email,
            isBusy: false
        };
    }

    handleClose = (alert?: IAlertProps): void => {
        this.props.onClose(alert);
    };

    handleChange = (e: IInputOnChangeEvent<string>): void => {
        this.setState({
            email: e.value,
            error: null
        });
    };

    handleBlur = (e: IInputOnBlurEvent): void => {
        if (e.wasChanged) {
            const email = this.state.email?.trim();
            const error = this.validateEmailDialogValue(email);
            this.setState({ email, error });
        }
    }

    validateEmailDialogValue = (email: string): IValidationError => {
        if (!email) {
            return { message: this.props.t("Common:Validation.Required") };
        }

        if (!EMAIL_REGEX.test(email)) {
            return { message: this.props.t("Common:Validation.WrongEmail") };
        }

        return null;
    };

    handleSendPayslipEmail = async (): Promise<void> => {
        const { storage } = this.props;
        const email = this.state.email.trim();
        const emailError = this.validateEmailDialogValue(email);

        if (emailError) {
            this.setState({ error: emailError });
            return;
        }

        this.setState({ isBusy: true, error: null });

        let alert: IAlertProps = null;
        try {
            const response = await customFetch(`${SEND_PAYSLIP_EMAIL_URL}/${storage.data.bindingContext.getKey()}?email=${encodeURIComponent(email)}`);
            if (!response.ok) {
                const error = await parseResponse(response) as ODataError;
                alert = getAlertFromError(error);
            } else {
                alert = {
                    ...getAlertPropsByStatus(Status.Success),
                    subTitle: this.props.t("Payslip:SendPayslipDialog.Success"),
                };
            }
        } catch (e) {
            alert = {
                ...getAlertPropsByStatus(Status.Error),
                subTitle: this.props.t("Common:Errors.UnknownError")
            };
        } finally {
            this.setState({ isBusy: false });
        }
        this.handleClose(alert);
    };

    render(): React.ReactElement {
        const { email, error, isBusy } = this.state;

        return (
            <Dialog
                title={this.props.t("Payslip:SendPayslipDialog.Title")}
                onConfirm={null}
                onClose={this.handleClose}
                busy={isBusy}
                footer={
                    <ConfirmationButtons
                        confirmText={this.props.t("Payslip:SendPayslipDialog.Send")}
                        onConfirm={this.handleSendPayslipEmail}
                        onCancel={this.handleClose}
                        isDisabled={isBusy}
                        useWrapper={false}
                    />
                }
            >
                <FieldsWrapper>
                    <Field label={this.props.t("Payslip:SendPayslipDialog.Email")} isRequired>
                        <Input
                            value={email ?? ""}
                            onChange={this.handleChange}
                            onBlur={this.handleBlur}
                            error={error}
                            width={BasicInputSizes.XL}
                        />
                    </Field>
                </FieldsWrapper>
            </Dialog>
        );
    }
}

export default withTranslation([
    "Payslip", "Common"
])(SendPayslipByEmailDialog);